import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { useToast } from '../ui/Toast';

interface FeatureRequestButtonProps {
  featureCategory: string;
  featureName: string;
  description: string;
}

export function FeatureRequestButton({
  featureCategory,
  featureName,
  description,
}: FeatureRequestButtonProps) {
  const { data: companyId } = useCurrentCompany();
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleRequest = async () => {
    if (!companyId) return;

    setIsSubmitting(true);
    try {
      const { error } = await (supabase as any)
        .from('feature_requests')
        .insert({
          company_id: companyId,
          feature_category: featureCategory,
          feature_name: featureName,
          description,
          priority: 'medium',
          status: 'requested',
        });

      if (error) {
        // If table doesn't exist (migration not applied), show info message
        if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
          showToast('Feature request system not available yet. Migrations may not be applied.', 'info');
          return;
        }
        throw error;
      }

      showToast('Feature request submitted! We\'ll prioritize based on demand.', 'success');
    } catch (error: any) {
      // Handle table not found gracefully
      if (error?.code === 'PGRST116' || error?.message?.includes('does not exist')) {
        showToast('Feature request system not available yet. Migrations may not be applied.', 'info');
        return;
      }
      console.error('Error submitting feature request:', error);
      showToast('Failed to submit feature request. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={handleRequest}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        disabled={isSubmitting}
        className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors text-sm"
      >
        <Info className="w-4 h-4" />
        <span>This feature is in progress</span>
      </button>
      {showTooltip && (
        <div className="absolute bottom-full left-0 mb-2 p-3 bg-[#363232] border border-[#504A4A] rounded-lg shadow-xl z-10 max-w-xs">
          <p className="text-xs text-text-primary mb-2">{description}</p>
          <p className="text-xs text-text-secondary">
            Click to request early access and help us prioritize
          </p>
        </div>
      )}
    </div>
  );
}
